/**
 * Payment Records Page - Admin
 * Comprehensive payment management and reporting for administrators
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  Download,
  Filter as FilterIcon,
  Search,
  Calendar,
  FileText,
} from 'lucide-react';
import { mockPayments, mockInvoices, getPaymentStatistics } from '../../data/payments';
import { PaymentCard, InvoiceCard } from '../../components/payment';
import { EmptyState } from '../../components/common/EmptyState';
import type { Payment, Invoice } from '../../types/payment';
import { PaymentStatus, InvoiceStatus, PaymentPurpose } from '../../types/payment';
import { formatCurrency } from '../../utils/formatting';

type ViewMode = 'payments' | 'invoices';
type DateFilter = 'all' | 'today' | 'week' | 'month' | 'year';

export const PaymentRecordsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewMode>('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | InvoiceStatus | 'ALL'>('ALL');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [purposeFilter, setPurposeFilter] = useState<PaymentPurpose | 'ALL'>('ALL');

  // Calculate overall statistics
  const statistics = useMemo(() => {
    const paymentStats = getPaymentStatistics();
    const totalInvoiced = mockInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const totalPaidInvoices = mockInvoices
      .filter((inv) => inv.status === InvoiceStatus.PAID)
      .reduce((sum, inv) => sum + inv.totalAmount, 0);

    return {
      ...paymentStats,
      totalInvoiced,
      totalPaidInvoices,
      invoiceCount: mockInvoices.length,
    };
  }, []);

  // Filter payments based on date
  const filterByDate = <T extends { createdAt: Date; paymentDate?: Date }>(items: T[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return items.filter((item) => {
      const itemDate = item.paymentDate || item.createdAt;
      const date = new Date(itemDate);

      switch (dateFilter) {
        case 'today':
          return date >= today;
        case 'week': {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return date >= weekAgo;
        }
        case 'month': {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return date >= monthAgo;
        }
        case 'year': {
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return date >= yearAgo;
        }
        default:
          return true;
      }
    });
  };

  // Filtered payments
  const filteredPayments = useMemo(() => {
    let filtered = mockPayments.filter((payment) => {
      const matchesSearch =
        payment.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.payer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.payee.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'ALL' || payment.status === (statusFilter as PaymentStatus);

      const matchesPurpose = purposeFilter === 'ALL' || payment.purpose === purposeFilter;

      return matchesSearch && matchesStatus && matchesPurpose;
    });

    return filterByDate(filtered);
  }, [searchTerm, statusFilter, purposeFilter, dateFilter]);

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    let filtered = mockInvoices.filter((invoice) => {
      const matchesSearch =
        invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.billTo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.billFrom.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'ALL' || invoice.status === (statusFilter as InvoiceStatus);

      return matchesSearch && matchesStatus;
    });

    return filterByDate(filtered);
  }, [searchTerm, statusFilter, dateFilter]);

  const handlePaymentClick = (payment: Payment) => {
    navigate(`/admin/payments/${payment.id}`);
  };

  const handleInvoiceClick = (invoice: Invoice) => {
    navigate(`/admin/invoices/${invoice.id}`);
  };

  const handleExportData = () => {
    alert(t('payments:payment.exportingData'));
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('payments:payment.records.title')}</h1>
          <p className="text-gray-600 mt-1">{t('payments:payment.records.subtitle')}</p>
        </div>
        <button
          onClick={handleExportData}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
        >
          <Download className="w-5 h-5" />
          {t('payments:payment.exportData')}
        </button>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-90">{t('payments:payment.totalRevenue')}</span>
          </div>
          <div className="text-3xl font-bold">
            {formatCurrency(statistics.totalRevenue, 'MYR')}
          </div>
          <div className="text-sm opacity-90 mt-1">
            {statistics.completedPayments} {t('payments:payment.completedTransactions')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <FileText className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-90">{t('payments:payment.totalInvoiced')}</span>
          </div>
          <div className="text-3xl font-bold">
            {formatCurrency(statistics.totalInvoiced, 'MYR')}
          </div>
          <div className="text-sm opacity-90 mt-1">
            {statistics.invoiceCount} {t('payments:invoice.invoices')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-90">{t('payments:payment.pendingPayments')}</span>
          </div>
          <div className="text-3xl font-bold">{statistics.pendingPayments}</div>
          <div className="text-sm opacity-90 mt-1">{t('payments:payment.awaitingProcessing')}</div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-90">{t('payments:payment.failedPayments')}</span>
          </div>
          <div className="text-3xl font-bold">{statistics.failedPayments}</div>
          <div className="text-sm opacity-90 mt-1">{t('payments:payment.requiresAttention')}</div>
        </div>
      </div>

      {/* View Mode Selector */}
      <div className="flex gap-2 bg-white rounded-lg border border-gray-200 p-1">
        <button
          onClick={() => setViewMode('payments')}
          className={`flex-1 px-4 py-2 rounded-md transition-colors ${
            viewMode === 'payments'
              ? 'bg-green-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <DollarSign className="w-4 h-4" />
            {t('payments:payment.payments')} ({mockPayments.length})
          </div>
        </button>
        <button
          onClick={() => setViewMode('invoices')}
          className={`flex-1 px-4 py-2 rounded-md transition-colors ${
            viewMode === 'invoices'
              ? 'bg-green-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <FileText className="w-4 h-4" />
            {t('payments:invoice.invoices')} ({mockInvoices.length})
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={
                  viewMode === 'payments'
                    ? t('payments:payment.searchPayments')
                    : t('payments:invoice.searchInvoices')
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <div className="relative">
              <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="ALL">{t('common.allStatuses')}</option>
                {viewMode === 'payments' ? (
                  <>
                    <option value={PaymentStatus.PENDING}>{t('payments:payment.status.pending')}</option>
                    <option value={PaymentStatus.PROCESSING}>
                      {t('payments:payment.status.processing')}
                    </option>
                    <option value={PaymentStatus.COMPLETED}>{t('payments:payment.status.completed')}</option>
                    <option value={PaymentStatus.FAILED}>{t('payments:payment.status.failed')}</option>
                  </>
                ) : (
                  <>
                    <option value={InvoiceStatus.DRAFT}>{t('payments:payment.invoiceStatus.draft')}</option>
                    <option value={InvoiceStatus.ISSUED}>
                      {t('payments:payment.invoiceStatus.issued')}
                    </option>
                    <option value={InvoiceStatus.PAID}>{t('payments:payment.invoiceStatus.paid')}</option>
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Date Filter */}
          <div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">{t('payments:payment.dateFilter.all')}</option>
                <option value="today">{t('payments:payment.dateFilter.today')}</option>
                <option value="week">{t('payments:payment.dateFilter.week')}</option>
                <option value="month">{t('payments:payment.dateFilter.month')}</option>
                <option value="year">{t('payments:payment.dateFilter.year')}</option>
              </select>
            </div>
          </div>
        </div>

        {viewMode === 'payments' && (
          <div className="mt-4">
            <div className="relative">
              <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={purposeFilter}
                onChange={(e) => setPurposeFilter(e.target.value as PaymentPurpose | 'ALL')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="ALL">{t('payments:payment.allPurposes')}</option>
                <option value={PaymentPurpose.APPLICATION_FEE}>
                  {t('payments:payment.purposes.application_fee')}
                </option>
                <option value={PaymentPurpose.CERTIFICATION_FEE}>
                  {t('payments:payment.purposes.certification_fee')}
                </option>
                <option value={PaymentPurpose.AUDIT_FEE}>{t('payments:payment.purposes.audit_fee')}</option>
                <option value={PaymentPurpose.TRAINING_FEE}>
                  {t('payments:payment.purposes.training_fee')}
                </option>
              </select>
            </div>
          </div>
        )}

        <div className="mt-3 text-sm text-gray-600">
          {viewMode === 'payments'
            ? t('payments:payment.showingResults', {
                count: filteredPayments.length,
                total: mockPayments.length,
              })
            : t('payments:invoice.showingResults', {
                count: filteredInvoices.length,
                total: mockInvoices.length,
              })}
        </div>
      </div>

      {/* Results */}
      {viewMode === 'payments' ? (
        <div className="space-y-3">
          {filteredPayments.map((payment) => (
            <PaymentCard
              key={payment.id}
              payment={payment}
              onClick={() => handlePaymentClick(payment)}
              showDetails
            />
          ))}
          {filteredPayments.length === 0 && (
            mockPayments.length === 0 ? (
              <EmptyState
                icon={<DollarSign className="w-10 h-10" />}
                title="No Payments Yet"
                description="No payment records have been created yet. Payment transactions will appear here once they are processed."
              />
            ) : (
              <EmptyState
                icon={<FilterIcon className="w-10 h-10" />}
                title="No Matching Payments"
                description="No payments match your current filters. Try adjusting the search term, status, or date filters."
                action={{
                  label: 'Clear All Filters',
                  onClick: () => {
                    setSearchTerm('');
                    setStatusFilter('ALL');
                    setPurposeFilter('ALL');
                    setDateFilter('all');
                  },
                }}
              />
            )
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              onClick={() => handleInvoiceClick(invoice)}
              showActions
            />
          ))}
          {filteredInvoices.length === 0 && (
            mockInvoices.length === 0 ? (
              <div className="col-span-full">
                <EmptyState
                  icon={<FileText className="w-10 h-10" />}
                  title="No Invoices Yet"
                  description="No invoices have been created yet. Invoices will appear here once they are generated for services."
                />
              </div>
            ) : (
              <div className="col-span-full">
                <EmptyState
                  icon={<FilterIcon className="w-10 h-10" />}
                  title="No Matching Invoices"
                  description="No invoices match your current filters. Try adjusting the search term, status, or date filters."
                  action={{
                    label: 'Clear All Filters',
                    onClick: () => {
                      setSearchTerm('');
                      setStatusFilter('ALL');
                      setDateFilter('all');
                    },
                  }}
                />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};
